import {
    getOrganizationClauseChecklists,
    IFilter,
    IOrganizationClauseChecklist,
} from "@/api";
import { SharedWithGroupLabel } from "@/components/shared/SharedWithGroupLabel";
import { CLAUSE_CHECKLIST_QUERY_FILTER } from "@/config/constants";
import { useDebouncedEffect } from "@react-hookz/web";
import { FormAutoCompleteElement } from "@rhf-kit/mui";
import { useQuery } from "@tanstack/react-query";
import { HTMLAttributes, Key, useEffect, useState } from "react";

interface IProps {
    /**
     * The organization id to fetch the clause checklists for
     */
    organizationId: number;
}

/**
 * A component that renders an autocomplete input for clause checklists for a specific organization
 */
export const ClauseChecklistAutocomplete = ({ organizationId }: IProps) => {
    const [clauseChecklists, setClauseChecklists] = useState<
        IOrganizationClauseChecklist[]
    >([]);

    const [search, setSearch] = useState<string>("");
    const [queryFilter, setQueryFilter] = useState<IFilter>(
        CLAUSE_CHECKLIST_QUERY_FILTER
    );

    const { data, isPending } = useQuery({
        queryKey: [
            "frontend-organization-clauseChecklists",
            organizationId,
            queryFilter,
        ],
        queryFn: () =>
            getOrganizationClauseChecklists(
                organizationId,
                1,
                100,
                queryFilter
            ),
    });

    useEffect(() => {
        if (data) {
            // Filter out clause checklists that have no clauses
            const filteredChecklists = data.items.filter(
                (checklist) =>
                    checklist.clauses.length > 0 || checklist.topics.length > 0
            );

            setClauseChecklists(filteredChecklists);
        }
    }, [data]);

    useDebouncedEffect(
        () => {
            if (search === "") {
                setQueryFilter(CLAUSE_CHECKLIST_QUERY_FILTER);
                return;
            }

            setQueryFilter({
                ...CLAUSE_CHECKLIST_QUERY_FILTER,
                q: search,
            });
        },
        [search],
        250,
        1000
    );

    return (
        <FormAutoCompleteElement
            label="Checklist"
            name="clause_checklist_id"
            options={clauseChecklists}
            size="small"
            fullWidth
            required
            autoComplete
            includeInputInList
            filterSelectedOptions
            getOptionLabel={(option: IOrganizationClauseChecklist) =>
                option.name
            }
            renderOption={(
                {
                    key,
                    ...optionProps
                }: HTMLAttributes<HTMLLIElement> & {
                    key: Key;
                },
                option: IOrganizationClauseChecklist
            ) => (
                <li key={key} {...optionProps}>
                    <span className="block min-w-0">
                        <span className="block">{option.name}</span>
                        <SharedWithGroupLabel
                            group={option.organization_group}
                        />
                    </span>
                </li>
            )}
            onInputChange={(_, newSearchValue) => setSearch(newSearchValue)}
            filterOptions={(x) => x}
            matchId
            loading={isPending}
        />
    );
};
